import { useState, useEffect, useMemo } from 'react';
import { X, Loader2, CalendarCheck, CheckCircle2, Circle, Search, Users, Save, ListChecks, CalendarDays } from 'lucide-react';
import { toast } from 'sonner';
import { getPartnerBatchAttendance, markPartnerBatchAttendance } from '@/api/stnApi';
import { cn } from '@/lib/utils';

const fmtDay = (d) => new Date(d).toLocaleDateString('en-IN', { weekday: 'short', day: 'numeric', month: 'short' });
const dayNum = (d) => new Date(d).getDate();

// Day-wise attendance marker (partner) — multi-date selection + calendar view +
// per-intern focus to fix missing days.
export default function AttendanceMarkModal({ batchId, batchName, onClose }) {
  const [data, setData]       = useState(null);
  const [loading, setLoading] = useState(true);
  const [view, setView]       = useState('calendar');
  const [search, setSearch]   = useState('');
  const [picked, setPicked]   = useState(new Set());
  const [days, setDays]       = useState(new Set());
  const [focus, setFocus]     = useState('');
  const [saving, setSaving]   = useState(null);

  const load = () => {
    setLoading(true);
    getPartnerBatchAttendance(batchId)
      .then((r) => { setData(r); setDays(new Set(r?.selectedDate ? [r.selectedDate] : [])); })
      .catch((e) => toast.error(e?.message || 'Could not load attendance'))
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [batchId]);

  const roster   = data?.roster || [];
  const dateList = data?.dates  || [];
  const dayCounts = data?.dayCounts || {};
  const focusRow = useMemo(() => roster.find(r => r.studentId === focus), [roster, focus]);
  const focusPresent = useMemo(() => new Set(focusRow?.presentDays || []), [focusRow]);
  const shown = roster.filter(r => !search || r.name.toLowerCase().includes(search.toLowerCase()) || r.email.toLowerCase().includes(search.toLowerCase()));

  const weeks = useMemo(() => {
    const m = {};
    dateList.forEach((d) => { (m[d.week] = m[d.week] || []).push(d); });
    return Object.entries(m).sort((a, b) => Number(a[0]) - Number(b[0]));
  }, [dateList]);

  const toggleDay = (date) => setDays(p => { const n = new Set(p); n.has(date) ? n.delete(date) : n.add(date); return n; });
  const toggleStudent = (id) => setPicked(p => { const n = new Set(p); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const allShownPicked = shown.length > 0 && shown.every(r => picked.has(r.studentId));
  const toggleAllStudents = () => setPicked(p => {
    if (allShownPicked) { const n = new Set(p); shown.forEach(r => n.delete(r.studentId)); return n; }
    const n = new Set(p); shown.forEach(r => n.add(r.studentId)); return n;
  });

  const targetIds = focus ? [focus] : [...picked];

  const save = async (present) => {
    if (days.size === 0) { toast.error('Select at least one date'); return; }
    if (targetIds.length === 0) { toast.error('Select at least one intern'); return; }
    setSaving(present ? 'present' : 'absent');
    try {
      const r = await markPartnerBatchAttendance(batchId, { dates: [...days], studentIds: targetIds, present });
      toast.success(r?.message || 'Attendance updated');
      load();
    } catch (e) { toast.error(e?.message || 'Could not save attendance'); }
    finally { setSaving(null); }
  };

  // One click: mark EVERY intern in the roster present for the selected day(s) —
  // no need to tick individual interns first.
  const markEveryone = async () => {
    if (days.size === 0) { toast.error('Select at least one date'); return; }
    const allIds = roster.map(r => r.studentId);
    if (!allIds.length) { toast.error('No interns to mark'); return; }
    setSaving('everyone');
    try {
      const r = await markPartnerBatchAttendance(batchId, { dates: [...days], studentIds: allIds, present: true });
      toast.success(r?.message || `Everyone marked present across ${days.size} day(s)`);
      load();
    } catch (e) { toast.error(e?.message || 'Could not save attendance'); }
    finally { setSaving(null); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center bg-black/50 p-0 sm:p-4 overflow-y-auto" onClick={onClose}>
      <div className="bg-card border border-border w-full max-w-2xl min-h-screen sm:min-h-0 sm:my-6 sm:rounded-2xl" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-4 border-b border-border sticky top-0 bg-card z-10 sm:rounded-t-2xl">
          <div className="flex items-center gap-2.5 min-w-0">
            <div className="w-9 h-9 rounded-xl bg-emerald-500/10 text-emerald-500 flex items-center justify-center shrink-0"><CalendarCheck className="w-4.5 h-4.5" /></div>
            <div className="min-w-0">
              <h3 className="font-bold text-foreground truncate">Mark Attendance</h3>
              <p className="text-xs text-muted-foreground truncate">{data?.batch?.title || batchName || 'Batch'} · select day(s) and interns</p>
            </div>
          </div>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground shrink-0"><X className="w-5 h-5" /></button>
        </div>

        {loading ? (
          <div className="flex items-center gap-2 py-16 justify-center text-muted-foreground text-sm"><Loader2 className="w-4 h-4 animate-spin" /> Loading roster…</div>
        ) : roster.length === 0 ? (
          <div className="text-center py-16 text-muted-foreground">
            <Users className="w-8 h-8 mx-auto mb-3 opacity-40" />
            <p className="text-sm">No enrolled interns to mark attendance for yet.</p>
          </div>
        ) : (
          <>
            <div className="px-5 py-3 border-b border-border flex items-center gap-3 flex-wrap bg-muted/30">
              <div className="inline-flex rounded-lg border border-border overflow-hidden">
                <button onClick={() => setView('calendar')} className={cn('px-3 h-8 text-xs font-semibold inline-flex items-center gap-1.5', view === 'calendar' ? 'bg-emerald-500 text-white' : 'bg-background text-muted-foreground')}><CalendarDays className="w-3 h-3" /> Calendar</button>
                <button onClick={() => setView('list')} className={cn('px-3 h-8 text-xs font-semibold inline-flex items-center gap-1.5', view === 'list' ? 'bg-emerald-500 text-white' : 'bg-background text-muted-foreground')}><ListChecks className="w-3 h-3" /> Days list</button>
              </div>
              <div className="ml-auto flex items-center gap-2">
                <label className="text-[11px] font-semibold text-muted-foreground">Focus intern</label>
                <select value={focus} onChange={e => setFocus(e.target.value)}
                  className="h-8 px-2 rounded-lg border border-border bg-background text-xs max-w-[180px]">
                  <option value="">All interns</option>
                  {roster.map(r => <option key={r.studentId} value={r.studentId}>{r.name}</option>)}
                </select>
              </div>
            </div>

            <div className="px-5 py-3">
              <div className="flex items-center justify-between mb-2">
                <p className="text-xs font-semibold text-muted-foreground">
                  {focus ? <>Pick the days to mark for <b className="text-foreground">{focusRow?.name}</b> — green = already present</> : 'Pick one or more days'}
                </p>
                {days.size > 0 && <button onClick={() => setDays(new Set())} className="text-[11px] text-muted-foreground hover:text-foreground">Clear ({days.size})</button>}
              </div>

              {view === 'calendar' ? (
                <div className="space-y-2 max-h-56 overflow-y-auto pr-1">
                  {weeks.map(([wk, ds]) => (
                    <div key={wk} className="flex items-center gap-2">
                      <span className="text-[10px] font-bold text-muted-foreground/50 w-8 shrink-0">W{wk}</span>
                      <div className="flex flex-wrap gap-1.5">
                        {ds.map((d) => {
                          const sel = days.has(d.date);
                          const isPresent = focus ? focusPresent.has(d.date) : (dayCounts[d.date] || 0) > 0;
                          const cnt = dayCounts[d.date] || 0;
                          return (
                            <button key={d.date} onClick={() => toggleDay(d.date)}
                              title={`${fmtDay(d.date)}${focus ? (focusPresent.has(d.date) ? ' · present' : ' · absent') : ` · ${cnt} present`}`}
                              className={cn('relative w-11 h-11 rounded-lg border text-center flex flex-col items-center justify-center transition-all',
                                sel ? 'border-emerald-500 bg-emerald-500 text-white ring-2 ring-emerald-500/30'
                                    : isPresent ? 'border-emerald-500/30 bg-emerald-500/10 text-emerald-500 hover:border-emerald-500/60'
                                                : 'border-border bg-background text-muted-foreground hover:border-muted-foreground/40')}>
                              <span className="text-[9px] leading-none opacity-70">{d.weekday}</span>
                              <span className="text-sm font-bold leading-none mt-0.5">{dayNum(d.date)}</span>
                              {!focus && cnt > 0 && <span className={cn('absolute -top-1 -right-1 text-[8px] font-bold rounded-full px-1', sel ? 'bg-white text-emerald-600' : 'bg-emerald-500 text-white')}>{cnt}</span>}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="flex flex-wrap gap-1.5 max-h-40 overflow-y-auto">
                  {dateList.map((d) => {
                    const sel = days.has(d.date);
                    return (
                      <button key={d.date} onClick={() => toggleDay(d.date)}
                        className={cn('text-[11px] font-medium px-2.5 py-1.5 rounded-full border transition-colors', sel ? 'bg-emerald-500 text-white border-emerald-500' : 'bg-background text-muted-foreground border-border hover:border-emerald-500/40')}>
                        {fmtDay(d.date)} · W{d.week}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>

            {!focus && (
              <>
                <div className="px-5 pb-2 flex items-center gap-3">
                  <div className="relative flex-1">
                    <Search className="w-3.5 h-3.5 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                    <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search interns…"
                      className="w-full pl-9 pr-3 h-9 rounded-lg border border-border bg-background text-sm focus:outline-none focus:border-emerald-400" />
                  </div>
                  <button onClick={toggleAllStudents}
                    className="h-9 px-3 rounded-lg border border-border text-xs font-semibold text-muted-foreground hover:bg-muted whitespace-nowrap">
                    {allShownPicked ? 'Unselect all' : 'Select all'}
                  </button>
                </div>
                <div className="max-h-[34vh] overflow-y-auto px-5">
                  <div className="divide-y divide-border/60 rounded-xl border border-border">
                    {shown.map(r => {
                      const sel = picked.has(r.studentId);
                      return (
                        <label key={r.studentId} className={cn('flex items-center gap-3 px-3 py-2.5 cursor-pointer transition-colors', sel ? 'bg-emerald-500/10' : 'hover:bg-muted/50')}>
                          <input type="checkbox" checked={sel} onChange={() => toggleStudent(r.studentId)} className="accent-emerald-600" />
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-semibold text-foreground truncate">{r.name}</p>
                            <p className="text-[11px] text-muted-foreground truncate">{r.email} · {r.attendancePct}% overall · {r.attendedDays} days</p>
                          </div>
                          <button type="button" onClick={(e) => { e.preventDefault(); setFocus(r.studentId); }}
                            className="text-[10px] font-semibold text-emerald-500 hover:underline shrink-0">View calendar</button>
                        </label>
                      );
                    })}
                    {shown.length === 0 && <p className="text-center py-6 text-muted-foreground text-sm">No interns match your search.</p>}
                  </div>
                </div>
              </>
            )}

            {focus && (
              <div className="px-5">
                <div className="rounded-xl border border-emerald-500/30 bg-emerald-500/5 px-3 py-2.5 flex items-center justify-between">
                  <div>
                    <p className="text-sm font-semibold text-foreground">{focusRow?.name}</p>
                    <p className="text-[11px] text-muted-foreground">{focusRow?.attendancePct}% overall · {focusRow?.presentDays?.length || 0} present in window</p>
                  </div>
                  <button onClick={() => setFocus('')} className="text-[11px] font-semibold text-muted-foreground hover:text-foreground">Back to all interns</button>
                </div>
              </div>
            )}

            <div className="px-5 py-4 mt-1 border-t border-border flex items-center gap-2 flex-wrap sticky bottom-0 bg-card">
              <p className="text-xs text-muted-foreground">
                <b className="text-foreground">{days.size}</b> day{days.size === 1 ? '' : 's'} · <b className="text-foreground">{focus ? 1 : picked.size}</b> intern{(focus ? 1 : picked.size) === 1 ? '' : 's'}
              </p>
              {!focus && (
                <button onClick={markEveryone} disabled={!!saving || days.size === 0 || roster.length === 0}
                  title={`Mark all ${roster.length} interns present for the selected day(s)`}
                  className="h-9 px-3 rounded-lg bg-emerald-500/10 text-emerald-600 border border-emerald-500/30 text-xs font-bold hover:bg-emerald-500/20 disabled:opacity-50 inline-flex items-center gap-1.5">
                  {saving === 'everyone' ? <Loader2 className="w-3 h-3 animate-spin" /> : <Users className="w-3 h-3" />} Mark everyone present ({roster.length})
                </button>
              )}
              <div className="ml-auto flex items-center gap-2">
                <button onClick={() => save(false)} disabled={!!saving || days.size === 0 || targetIds.length === 0}
                  className="h-9 px-3 rounded-lg border border-border text-xs font-semibold text-muted-foreground hover:bg-muted disabled:opacity-50 inline-flex items-center gap-1.5">
                  {saving === 'absent' ? <Loader2 className="w-3 h-3 animate-spin" /> : <Circle className="w-3 h-3" />} Mark Absent
                </button>
                <button onClick={() => save(true)} disabled={!!saving || days.size === 0 || targetIds.length === 0}
                  className="h-9 px-4 rounded-lg bg-emerald-500 text-white text-xs font-bold hover:bg-emerald-600 disabled:opacity-50 inline-flex items-center gap-1.5">
                  {saving === 'present' ? <Loader2 className="w-3 h-3 animate-spin" /> : <Save className="w-3 h-3" />} Mark Present
                </button>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
